import React, { useRef } from 'react';
import { X } from 'lucide-react';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** 'peek' shows a partial sheet; 'full' expands to ~80vh */
  height?: 'peek' | 'full';
}

export function BottomSheet({ open, onClose, title, children, height = 'full' }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[900] flex flex-col justify-end pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 pointer-events-auto"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative pointer-events-auto bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: height === 'full' ? '82vh' : '60vh' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-2.5 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 py-3">{children}</div>
      </div>
    </div>
  );
}
